// Parse a curl command OR JSON snippet into { apiBase, bearer }.
// Used by AddCompanyModal and SessionSettingsModal.
//
// Server is the source of truth — it also re-validates the parse and
// probes <api_base>/profile before committing to clients.json. This
// client-side parser is for live preview only.

export interface ParsedCredential {
  apiBase: string;
  bearer: string;
}

export interface ParseResult {
  ok: boolean;
  value?: ParsedCredential;
  error?: string;
}

/* =========================================================================
   Public API
   ========================================================================= */
export function parseCurlOrJson(raw: string): ParseResult {
  const text = raw.trim();
  if (!text) return { ok: false, error: "Paste a curl command or JSON to continue." };

  // If the user clearly pasted JSON (starts with { or [), only try JSON
  // and return its specific error — don't fall through to curl (which
  // produces a confusing "no Bearer in curl" message for valid JSON
  // with a nested agent_key).
  const looksLikeJson = text.startsWith("{") || text.startsWith("[");
  if (looksLikeJson) {
    const jsonAttempt = tryParseJson(text);
    if (jsonAttempt.ok) return validate(jsonAttempt.value!);
    return { ok: false, error: jsonAttempt.error ?? "JSON parse failed." };
  }

  // Otherwise try curl, then fall back to JSON.
  const curlAttempt = tryParseCurl(text);
  if (curlAttempt.ok) return validate(curlAttempt.value!);
  const jsonAttempt = tryParseJson(text);
  if (jsonAttempt.ok) return validate(jsonAttempt.value!);
  return {
    ok: false,
    error: curlAttempt.error ?? jsonAttempt.error ?? "Couldn't parse as curl or JSON.",
  };
}

/* =========================================================================
   Validation — same rules the server enforces (defense in depth)
   ========================================================================= */
function validate(v: ParsedCredential): ParseResult {
  let url: URL;
  try {
    url = new URL(v.apiBase);
  } catch {
    return { ok: false, error: "API base isn't a valid URL." };
  }
  if (url.protocol !== "https:") {
    return { ok: false, error: "API base must use https://" };
  }
  // Hostname check intentionally permissive — clients can run the
  // Reputifly plugin on their own domains (e.g. esgworlds.com) or on a
  // *.reputifly.cloud subdomain. The bearer token + server-side
  // /profile probe is what actually authenticates the credential.
  if (!url.hostname.includes(".")) {
    return { ok: false, error: "API base hostname looks invalid (no dot)" };
  }
  if (!v.bearer || /\s/.test(v.bearer)) {
    return { ok: false, error: "Bearer token is missing or contains whitespace." };
  }
  if (v.bearer.length < 16) {
    return { ok: false, error: "Bearer token is too short (under 16 characters)." };
  }
  return { ok: true, value: { apiBase: normalizeApiBase(v.apiBase), bearer: v.bearer } };
}

/**
 * Normalize any Archimedes URL to the `/agent` base.
 * Mirrors backend's clientsJson.normalizeApiBase exactly.
 *   curl pointing at /profile, /posts, /pages, /media   → base ends at /agent
 *   curl pointing at just https://imi.reputifly.cloud/  → infers /wp-json/reputifly/v1/agent
 */
function normalizeApiBase(raw: string): string {
  let url = raw.replace(/\/+$/, "");
  const m = url.match(/^(.*\/wp-json\/reputifly\/v\d+\/agent)(\/.*)?$/i);
  if (m && m[1]) return m[1];
  try {
    const u = new URL(url);
    if (!u.pathname.includes("/wp-json/")) {
      return `${u.origin}/wp-json/reputifly/v1/agent`;
    }
  } catch {
    /* fall through */
  }
  return url;
}

/* =========================================================================
   JSON parser — accepts a few common field-name variations
   ========================================================================= */
function tryParseJson(text: string): ParseResult {
  if (!text.startsWith("{") && !text.startsWith("[")) {
    return { ok: false, error: "Not JSON (doesn't start with { or [)" };
  }
  let obj: any;
  try {
    obj = JSON.parse(text);
  } catch (e: any) {
    return { ok: false, error: `Invalid JSON: ${e?.message ?? "parse failed"}` };
  }
  // If array, take the first element.
  if (Array.isArray(obj)) obj = obj[0];
  if (!obj || typeof obj !== "object") {
    return { ok: false, error: "JSON didn't decode to an object." };
  }
  // api_base: prefer the explicit field; fall back to deriving from
  // site_url (the Reputifly agent-profile JSON exports `site_url` at
  // top level + the api path under endpoint_index, so site_url alone
  // is enough to compute the /wp-json/reputifly/v1/agent endpoint).
  const apiBase =
    obj.api_base ?? obj.apiBase ?? obj.url ?? obj.endpoint ?? obj.base_url ?? obj.site_url ?? null;
  // bearer: top-level OR nested under `authentication.agent_key`
  // (latest Reputifly plugin profile-export shape).
  const auth = obj.authentication && typeof obj.authentication === "object" ? obj.authentication : null;
  const bearer =
    obj.agent_key ?? obj.agentKey ?? obj.bearer ?? obj.token ?? obj.access_token ??
    auth?.agent_key ?? auth?.agentKey ?? auth?.bearer ?? auth?.token ?? null;
  if (typeof apiBase !== "string" || typeof bearer !== "string") {
    return {
      ok: false,
      error: "JSON is missing `api_base` (or `site_url`) and/or `agent_key` (top-level or under `authentication`).",
    };
  }
  return { ok: true, value: { apiBase, bearer } };
}

/* =========================================================================
   Curl parser — extracts the first URL and the Bearer token
   ========================================================================= */
function tryParseCurl(text: string): ParseResult {
  // Collapse line continuations.
  const collapsed = text.replace(/\\\s*\n/g, " ");

  // Find URL — prefer https://...reputifly. but accept any https://.
  // Strip surrounding quotes if present.
  const urlMatch = collapsed.match(/https:\/\/[^\s"'`]+/i);
  if (!urlMatch) {
    return { ok: false, error: "Couldn't find an https:// URL in the curl." };
  }
  let url = urlMatch[0];
  // Strip a trailing comma, semicolon, or closing quote that snuck in.
  url = url.replace(/[,;"'`]+$/, "");

  // Find Bearer — case-insensitive.
  const bearerMatch = collapsed.match(
    /(?:Authorization\s*:\s*)?Bearer\s+([A-Za-z0-9._\-+/=]+)/i,
  );
  if (!bearerMatch) {
    return {
      ok: false,
      error: "Couldn't find a `Bearer <token>` in the curl (check Authorization header).",
    };
  }
  const bearer = bearerMatch[1];

  return { ok: true, value: { apiBase: url, bearer } };
}
